'use es6';

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import * as extensions from '../../../lib/api-explorer/oas-extensions';
import { ACTIONS, EVENTS } from '../../../tracking/events';
import Oas, { OperationPropType } from '../../../lib/api-explorer/models/Oas';
import generateCodeSnippet, { getHighlight } from '../../../lib/api-explorer/generate-code-snippet';
import generateSdkCodeSnippet from '../../../lib/api-explorer/generate-sdk-code-snippet';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H5 from 'UIComponents/elements/headings/H5';
import UnderConstruction from '../../Doc/components/UnderConstruction';
import { getSecurityTypes } from '../../Doc/selectors/DocSelectors';
import TryIt from '../../TryIt/components/TryIt';
import { AuthTypePropType } from '../../TryIt/constants';
import { getOAuthToken } from '../../TryIt/selectors';
import CodeMirror from './CodeMirror';
import LanguagePicker from './LanguagePicker';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledCodeMirror = styled(CodeMirror).withConfig({
  displayName: "CodeSample__StyledCodeMirror",
  componentId: "pa43kh-0"
})([".CodeMirror-wrap{.CodeMirror-line{word-break:break-word;}}"]);
const CodeSample = ({
  doc,
  examples,
  language,
  setLanguage,
  har,
  oas,
  oasDocs,
  operation,
  isMultipart,
  securityTypes,
  availableClientLibraries,
  oAuthToken,
  usageTracker
}) => {
  const [code, setCode] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const hasMethodName = operationId => {
    if (operationId.split('_').length <= 1) {
      return false;
    }
    return true;
  };
  useEffect(() => {
    if (availableClientLibraries.includes(language) && hasMethodName(operation.operationId)) {
      // There is sdk support for this language, use snippets-gen
      const newHighlight = getHighlight(language);
      const codeSnippet = generateSdkCodeSnippet(har, language, oas, operation.operationId, {
        oAuthToken
      });
      setCode(codeSnippet);
      setHighlight(newHighlight);
    } else {
      // There isn't sdk support for this language, use regular code-snippets
      const {
        codeSnippet,
        codeHighlight
      } = generateCodeSnippet(har, language);
      setCode(codeSnippet);
      setHighlight(codeHighlight);
    }
  }, [har, language, oas, operation, availableClientLibraries, oAuthToken]);
  const getTrackerData = () => {
    return {
      language,
      method: operation.method.toUpperCase(),
      endpoint: `${operation.oas.servers[0].url}${operation.path}`
    };
  };
  const onCopy = () => {
    usageTracker.track(EVENTS.REQUEST_INTERACTION, Object.assign({
      action: ACTIONS.COPY_REQUEST
    }, getTrackerData()));
  };
  const onSetLanguage = newLang => {
    usageTracker.track(EVENTS.REQUEST_INTERACTION, Object.assign({
      action: ACTIONS.CHANGE_LANGUAGE
    }, getTrackerData()));
    setLanguage(newLang);
  };
  const renderSelected = () => {
    return /*#__PURE__*/_jsx(UnderConstruction, {
      component: "Examples"
    });
  };
  const renderHeader = () => {
    return /*#__PURE__*/_jsxs("div", {
      className: "code-request-header",
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-shared-components.request.header"
        })
      }), /*#__PURE__*/_jsx(TryIt, {
        docId: doc._id,
        oasDocs: oasDocs,
        securityTypes: securityTypes,
        isMultipart: isMultipart,
        usageTracker: usageTracker
      })]
    });
  };
  const renderContent = () => {
    if (examples.length) {
      return renderSelected(examples, onSetLanguage);
    }
    if (!oas[extensions.SAMPLES_ENABLED] || isMultipart) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [renderHeader(), /*#__PURE__*/_jsx("div", {
          className: "hub-no-code m-bottom-2",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-shared-components.codeSample.noSamples"
          })
        })]
      });
    }
    return /*#__PURE__*/_jsxs(View, {
      "data-test-id": "code-sample",
      marginBlockEnd: "800",
      as: "div",
      children: [renderHeader(), /*#__PURE__*/_jsx(LanguagePicker, {
        languages: oas[extensions.SAMPLES_LANGUAGES],
        onCopyValue: onCopy,
        selectedLanguage: language,
        setLanguage: onSetLanguage,
        value: code
      }), code && /*#__PURE__*/_jsx(StyledCodeMirror, {
        value: code,
        mode: highlight,
        height: "300px"
      })]
    });
  };
  return /*#__PURE__*/_jsx("div", {
    className: "code-sample",
    children: renderContent()
  });
};
CodeSample.propTypes = {
  doc: PropTypes.shape({
    _id: PropTypes.string
  }),
  examples: PropTypes.arrayOf(PropTypes.shape({
    language: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired
  })),
  formData: PropTypes.shape({}).isRequired,
  har: PropTypes.object,
  isMultipart: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  oAuthToken: PropTypes.string,
  oas: PropTypes.instanceOf(Oas).isRequired,
  oasDocs: PropTypes.array,
  operation: OperationPropType.isRequired,
  securityTypes: PropTypes.arrayOf(AuthTypePropType).isRequired,
  setLanguage: PropTypes.func.isRequired,
  availableClientLibraries: PropTypes.array.isRequired,
  usageTracker: PropTypes.object.isRequired
};
const mapStateToProps = (state, ownProps) => {
  return {
    oAuthToken: getOAuthToken(state),
    securityTypes: getSecurityTypes(state, ownProps).sort().reverse()
  };
};
export default connect(mapStateToProps, null)(CodeSample);